import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { DEVICE_MANAGEMENT_CONTAINERS_URL } from '../../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../../common/ticket-api-get-list.params'
import { DeviceManagementContainerDto } from '../../../dto/device/device-management/device-management-containers/DeviceManagementContainer.dto'
import { mapSortContainerParam } from './device-management-container-sort.mapper'
import filterMapper from './device-management-container-filter.mapper'
import { addTreeMethodsBasedOnParentAndPosition } from '../../../../lib/@react-admin/ra-tree/esm/src'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<DeviceManagementContainerDto>> => {
    const filterParams = `c=>${filterParamsComposer('c', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortContainerParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<DeviceManagementContainerDto[]>(
      `${DEVICE_MANAGEMENT_CONTAINERS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<DeviceManagementContainerDto>> => {
    const data = await get<DeviceManagementContainerDto>(
      DEVICE_MANAGEMENT_CONTAINERS_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<DeviceManagementContainerDto>> => {
    const data = await get<DeviceManagementContainerDto[]>(
      `${DEVICE_MANAGEMENT_CONTAINERS_URL}/GetByConditions`,
      `/u=>new int[] {${ids.toString()}}.Contains(u.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<DeviceManagementContainerDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      DeviceManagementContainerDto
    >(DEVICE_MANAGEMENT_CONTAINERS_URL, {
      id,
      name: data.name !== previousData.name ? data.name : undefined,
      backgroundImageId:
        data.backgroundImageId !== previousData.backgroundImageId
          ? data.backgroundImageId
          : undefined,
      parentId:
        data.parentId !== previousData.parentId ? data.parentId : undefined,
    })
    return Promise.resolve({ data: update })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateContainerRequest>,
  ): Promise<CreateResult<DeviceManagementContainerDto>> => {
    const container = await post<
      CreateContainerRequest[],
      DeviceManagementContainerDto[]
    >(DEVICE_MANAGEMENT_CONTAINERS_URL, [data])
    return {
      data: container[0],
    }
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult<DeviceManagementContainerDto>> => {
    const deleted = await remove<DeviceManagementContainerDto>(
      DEVICE_MANAGEMENT_CONTAINERS_URL,
      id as string,
    )
    return {
      data: deleted,
    }
  },
} as DataProvider

export interface CreateContainerRequest {
  readonly name: string
  readonly backgroundImageId: number
  readonly parentId: number
}

export default addTreeMethodsBasedOnParentAndPosition(
  provider,
  'parentId',
  'id',
  false,
)
